import { DatasourceCanBaseIcon } from '@tableau/icons/DatasourceCanBaseIcon';

import { Filter } from '~commonInfra/utils/Filter';
import { ServerFilterClauses } from '~commonInfra/utils/ServerFilterClauses';
import { Messages } from '~src/codegen/Localize';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { GetDependentDataSourcesRequest } from '~src/datasource/ServerApi/requests/GetDependentDataSourcesRequest';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { BaseParamsBuilderResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { GenericCard } from '~src/spacesPlatform/components/card/GenericCard';
import { ContentTypeFilterClause, ExplorableItemDefinition } from '~src/spacesPlatform/contentDefinitionInterfaces/ContentDefinitionsTypes';
import { getGenericDefaultFilters } from '~src/spacesPlatform/contentDefinitions/getDefaultFilters';
import { DatasourceContentType } from '~src/spacesPlatform/models/ContentTypes';

export class DependentDataSourcesResourceQuery extends BaseParamsBuilderResourceQuery<IDataSourceInfo> {
  private dataSourceId: string;
  private cachedDependentIds: string[] | null = null;

  constructor(dataSourceId: string) {
    super();
    this.dataSourceId = dataSourceId;
  }

  public async getSlice(start: number, size: number): Promise<ListResult<IDataSourceInfo>> {
    let dependentIds: string[];
    if (this.cachedDependentIds !== null) {
      dependentIds = this.cachedDependentIds;
    } else {
      const request = new GetDependentDataSourcesRequest({ dataSourceId: this.dataSourceId });
      const result = await WebHttpClient.sendRequest(request);
      dependentIds = result.dependentDataSourceIds || [];
      this.cachedDependentIds = dependentIds;
    }

    if (dependentIds.length === 0) {
      return new ListResult<IDataSourceInfo>([], start, 0);
    }

    const datasourcesQuery = DataSources.all()
      .withFilters([
        Filter.stringFieldInArray('id', dependentIds),
        ...this.getFilters()
      ])
      .withOrder(this.getOrder())
      .withExtraParams(this.getExtraParams());

    return await datasourcesQuery.getSlice(start, size);
  }

  public async getFirst(): Promise<IDataSourceInfo | null> {
    const result = await this.getSlice(0, 1);
    return result.items && result.items.length > 0 ? result.items[0] : null;
  }

  public async getCount(): Promise<number> {
    if (this.cachedDependentIds !== null) {
      return this.cachedDependentIds.length;
    }
    const request = new GetDependentDataSourcesRequest({ dataSourceId: this.dataSourceId });
    const result = await WebHttpClient.sendRequest(request);
    const dependentIds = result.dependentDataSourceIds || [];
    this.cachedDependentIds = dependentIds;
    return dependentIds.length;
  }
}

export const DependentDataSourcesInDatasourceDefinition: ExplorableItemDefinition = {
  itemType: 'dependentDataSource',
  pluralName: Messages.datasources,
  singularName: Messages.datasource,
  emptyStateMessage: Messages.datasourcesEmptyMessage,
  contentFilterKeys: DatasourceContentType.filterKeys,
  defaultFilters: getGenericDefaultFilters,
  cardComponent: GenericCard,
  listViewIconType: DatasourceCanBaseIcon,
  filterClause: ServerFilterClauses.ContentType(['datasource']) as ContentTypeFilterClause,
  getBasicQuery: () => DataSources.all().withFilters([Filter.stringFieldInArray('id', [])]),
  getContentInfo: () => {
    // No special content info processing needed
  },
  supportedDisplayModes: ['list', 'thumbnail'],
};
